import { Outlet } from '@tanstack/react-router';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import { useMode } from '@heyroot/ui/theme';
import { Sun, Moon } from 'lucide-react';
import { useAdminAuth } from '../lib/auth-context';
import { Sidebar } from './sidebar';
import { CommandPalette } from './command-palette';

export function AppShell() {
  const { status } = useAdminAuth();
  const { resolvedMode, setMode } = useMode();

  // During the auth-bootstrap window (/api/me in flight) render the shell chrome
  // immediately with a skeleton sidebar rail + content area, so the app frame
  // appears instantly and only the content region shows a skeleton.
  if (status === 'loading') {
    return (
      <div className="flex h-screen overflow-hidden bg-surface">
        {/* Skeleton sidebar rail — matches real sidebar width/structure */}
        <nav
          aria-hidden="true"
          className="flex w-52 shrink-0 flex-col h-full border-r border-border bg-surface py-3 px-2"
        >
          <div className="px-2 pb-4 mb-2 border-b border-border">
            <Skeleton className="h-3 w-28" />
          </div>
          <ul role="list" className="flex-1 space-y-1 list-none p-0 m-0">
            {Array.from({ length: 3 }).map((_, i) => (
              <li key={i} className="px-2.5 py-1.5">
                <Skeleton className="h-4 w-full" style={{ width: `${60 + i * 10}%` }} />
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex flex-1 min-w-0 overflow-hidden flex-col">
          {/* Top bar chrome (same structure as real shell) */}
          <header className="flex h-14 shrink-0 items-center justify-end gap-3 border-b border-border bg-surface-elevated px-5">
            <Skeleton className="h-4 w-20 rounded-control" />
            <Skeleton className="h-4 w-32 rounded-control" />
            <Skeleton className="h-7 w-7 rounded-control" />
          </header>

          {/* Content area skeleton */}
          <main className="flex-1 overflow-y-auto p-6">
            <div className="flex flex-col gap-4 max-w-[900px]">
              <Skeleton className="h-7 w-40" />
              <Skeleton className="h-4 w-24" />
              <div className="rounded-control border border-border bg-surface-elevated p-5 flex flex-col gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-4" style={{ width: `${45 + (i % 3) * 20}%` }} />
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-control focus:bg-surface-elevated focus:px-3 focus:py-2 focus:text-body-sm focus:text-primary"
      >
        Skip to main content
      </a>
      <Sidebar />

      <div className="flex flex-1 min-w-0 overflow-hidden flex-col">
        {/* Top bar */}
        <header className="flex h-14 shrink-0 items-center justify-end gap-3 border-b border-border bg-surface-elevated px-5">
          {/* eslint-disable-next-line @heyroot/heyroot-ui/no-one-off-ui -- design-exception: icon-only affordance; Button primitive has no icon-only size */}
          <button
            type="button"
            onClick={() => setMode(resolvedMode === 'dark' ? 'light' : 'dark')}
            className="rounded-control p-1.5 text-muted hover:bg-surface-sunken hover:text-primary transition-colors duration-[var(--duration-fast)]"
            aria-label={`Switch to ${resolvedMode === 'dark' ? 'light' : 'dark'} mode`}
          >
            {resolvedMode === 'dark' ? (
              <Sun aria-hidden="true" className="h-4 w-4" />
            ) : (
              <Moon aria-hidden="true" className="h-4 w-4" />
            )}
          </button>
        </header>

        {/* Page content */}
        <main id="main-content" className="flex-1 overflow-y-auto p-6" tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      <CommandPalette />
    </div>
  );
}
